import type { CreativeGenome } from '../types'

/**
 * Pure helpers for the Creative Genome panel — kept free of React so the parse/validation and
 * swatch-extraction logic can be unit tested in isolation.
 */

export type ParseResult =
  | { ok: true; value: CreativeGenome }
  | { ok: false; error: string }

/**
 * Parse operator-edited JSON into a CreativeGenome. The genome is stored verbatim as jsonb, so the
 * only hard requirement is that the top level is a plain object (not an array / primitive).
 */
export function parseGenome(text: string): ParseResult {
  const trimmed = text.trim()
  if (!trimmed) {
    return { ok: false, error: 'Genome cannot be empty — provide a JSON object.' }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (e) {
    return { ok: false, error: `Invalid JSON: ${(e as Error).message}` }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Genome must be a JSON object (e.g. { "concept": "…" }).' }
  }
  return { ok: true, value: parsed as CreativeGenome }
}

/** Pretty-print a genome for the editor. */
export function stringifyGenome(genome: CreativeGenome | null): string {
  return JSON.stringify(genome ?? {}, null, 2)
}

const COLOR_RE = /^(#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\([^)]*\)|hsla?\([^)]*\))$/i

/** Whether a string looks like a CSS color value (hex / rgb(a) / hsl(a)). */
export function isColorValue(value: unknown): value is string {
  return typeof value === 'string' && COLOR_RE.test(value.trim())
}

export interface Swatch {
  label: string
  value: string
}

/**
 * Walk the (free-form) design `tokens` object and pull out anything that looks like a color, labelled
 * by its key path (e.g. "palette.ink"). Bounded in depth + count so a large token tree stays legible.
 */
export function extractSwatches(tokens: unknown, max = 24): Swatch[] {
  const out: Swatch[] = []

  const walk = (node: unknown, path: string, depth: number) => {
    if (out.length >= max || depth > 4 || node == null) return
    if (isColorValue(node)) {
      out.push({ label: path || 'color', value: (node as string).trim() })
      return
    }
    if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, path ? `${path}[${i}]` : `[${i}]`, depth + 1))
      return
    }
    if (typeof node === 'object') {
      for (const [key, val] of Object.entries(node as Record<string, unknown>)) {
        walk(val, path ? `${path}.${key}` : key, depth + 1)
      }
    }
  }

  walk(tokens, '', 0)
  return out
}
